import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '..';
import type { SnowflakeConfigurationItem } from '@server/snowflake-documentation';
import { SnowflakeParentType } from '@server/snowflake-documentation';
import {
  loadTabsForOrg,
  saveTabsForOrg,
  SNOWFLAKE_TABS_STORAGE_KEY,
} from '../../lib/documentationTabsLocalStorage';

type ElementsByType = Partial<Record<SnowflakeParentType, SnowflakeConfigurationItem[]>>;

export const SNOWFLAKE_DEFAULT_TAB = SnowflakeParentType.TABLE;

export interface SnowflakeDocumentationState {
  elementsByOrg: {
    [snowflakeOrgId: string]: ElementsByType;
  };
  loadingByOrg: {
    [snowflakeOrgId: string]: Partial<Record<SnowflakeParentType, boolean>>;
  };
  activeParentType: SnowflakeParentType;
  openTabs: SnowflakeParentType[];
}

const initialState: SnowflakeDocumentationState = {
  elementsByOrg: {},
  loadingByOrg: {},
  activeParentType: SNOWFLAKE_DEFAULT_TAB,
  openTabs: [SNOWFLAKE_DEFAULT_TAB],
};

export const snowflakeDocumentationSlice = createSlice({
  name: 'snowflakeDocumentation',
  initialState,
  reducers: {
    setSnowflakeElements: (
      state,
      action: PayloadAction<{
        snowflakeOrgId: string;
        parentType: SnowflakeParentType;
        elements: SnowflakeConfigurationItem[];
      }>,
    ) => {
      const { snowflakeOrgId, parentType, elements } = action.payload;
      if (!state.elementsByOrg[snowflakeOrgId]) {
        state.elementsByOrg[snowflakeOrgId] = {};
      }
      state.elementsByOrg[snowflakeOrgId][parentType] = elements;
    },

    setSnowflakeLoading: (
      state,
      action: PayloadAction<{
        snowflakeOrgId: string;
        parentType: SnowflakeParentType;
        isLoading: boolean;
      }>,
    ) => {
      const { snowflakeOrgId, parentType, isLoading } = action.payload;
      if (!state.loadingByOrg[snowflakeOrgId]) {
        state.loadingByOrg[snowflakeOrgId] = {};
      }
      state.loadingByOrg[snowflakeOrgId][parentType] = isLoading;
    },

    clearSnowflakeElements: (state, action: PayloadAction<{ snowflakeOrgId: string }>) => {
      delete state.elementsByOrg[action.payload.snowflakeOrgId];
      delete state.loadingByOrg[action.payload.snowflakeOrgId];
    },

    setSnowflakeActiveParentType: (state, action: PayloadAction<SnowflakeParentType>) => {
      state.activeParentType = action.payload;
    },

    loadSnowflakeTabsForOrg: (state, action: PayloadAction<{ snowflakeOrgId: string }>) => {
      const stored = loadTabsForOrg<SnowflakeParentType[]>(
        SNOWFLAKE_TABS_STORAGE_KEY,
        action.payload.snowflakeOrgId,
      );
      if (stored?.length) {
        const withDefault = stored.includes(SNOWFLAKE_DEFAULT_TAB)
          ? stored
          : [SNOWFLAKE_DEFAULT_TAB, ...stored];
        state.openTabs = withDefault;
        state.activeParentType = withDefault[0];
      }
    },

    addSnowflakeTab: (
      state,
      action: PayloadAction<{ parentType: SnowflakeParentType; snowflakeOrgId: string }>,
    ) => {
      const { parentType, snowflakeOrgId } = action.payload;
      if (!state.openTabs.includes(parentType)) {
        state.openTabs.push(parentType);
      }
      state.activeParentType = parentType;
      const userTabs = state.openTabs.filter((t) => t !== SNOWFLAKE_DEFAULT_TAB);
      saveTabsForOrg(SNOWFLAKE_TABS_STORAGE_KEY, snowflakeOrgId, userTabs);
    },

    removeSnowflakeTab: (
      state,
      action: PayloadAction<{ parentType: SnowflakeParentType; snowflakeOrgId: string }>,
    ) => {
      const { parentType, snowflakeOrgId } = action.payload;
      if (parentType === SNOWFLAKE_DEFAULT_TAB) return;
      state.openTabs = state.openTabs.filter((t) => t !== parentType);
      if (state.activeParentType === parentType) {
        state.activeParentType = state.openTabs[state.openTabs.length - 1] ?? SNOWFLAKE_DEFAULT_TAB;
      }
      const userTabs = state.openTabs.filter((t) => t !== SNOWFLAKE_DEFAULT_TAB);
      saveTabsForOrg(SNOWFLAKE_TABS_STORAGE_KEY, snowflakeOrgId, userTabs);
    },
  },
});

export const {
  setSnowflakeElements,
  setSnowflakeLoading,
  clearSnowflakeElements,
  setSnowflakeActiveParentType,
  addSnowflakeTab,
  removeSnowflakeTab,
  loadSnowflakeTabsForOrg,
} = snowflakeDocumentationSlice.actions;

export const selectSnowflakeElements =
  (snowflakeOrgId: string, parentType: SnowflakeParentType) => (state: RootState) =>
    state[snowflakeDocumentationSlice.name]?.elementsByOrg[snowflakeOrgId]?.[parentType];

export const selectSnowflakeLoading =
  (snowflakeOrgId: string, parentType: SnowflakeParentType) => (state: RootState) =>
    state[snowflakeDocumentationSlice.name]?.loadingByOrg[snowflakeOrgId]?.[parentType] ?? false;

export const selectAllSnowflakeElements = (snowflakeOrgId: string) => (state: RootState) =>
  state[snowflakeDocumentationSlice.name]?.elementsByOrg[snowflakeOrgId];

export const selectSnowflakeActiveParentType = (state: RootState) =>
  state[snowflakeDocumentationSlice.name]?.activeParentType ?? SNOWFLAKE_DEFAULT_TAB;

export const selectSnowflakeOpenTabs = (state: RootState) =>
  state[snowflakeDocumentationSlice.name]?.openTabs ?? [SNOWFLAKE_DEFAULT_TAB];

export default snowflakeDocumentationSlice.reducer;
